'use client';

import { css, stack } from '@bts-web/utils-style-engine';
import { DialogBase, HyperLinkButtonBase } from './../../../common';
import { ModalComponent } from '../ModalDrawerContext/ModalDrawerContext';
import { useClientTranslation } from '@bts-web/utils-lokalise';
import { RegularDismissCircle } from '../../svgIcons/RegularDismissCircle';
import {
  CreateTradeOfferMutation$data,
  CreateTradeOfferErrorCode,
} from '@bts-web/data-layer/server';
import { getSegmentLimitTranslations } from '@bts-web/core-features/trade';
import { SegmentLimitsTranslations } from '../../../trade/CreateTrade/types';
import { TFunction } from 'i18next';
import { intlFiatValueFormatting } from '@bts-web/utils-formatting';
import { getAppConfig } from '@bts-web/core-app-config';
import { NEXT_ROUTES } from '@bts-web/utils-next-infrastructure';
import { removeInputValueFromLocalStorage } from '../../../trade/CreateTrade/utils/localStorageInputValueUtils';
import { getAssetTradeUrl } from '../../../route-builders';

const containerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
  mx: 'auto',
  flex: '1',
});

type SegmentLimitsModalProps = ModalComponent<{
  type: CreateTradeOfferErrorCode;
  details: NonNullable<
    CreateTradeOfferMutation$data['createTradeOffer']
  >['details'];
  assetId: string;
}>;

type ModalParams = {
  title: string;
  limitAvailable: string;
  limitReached: string;
  limitContinuation: string;
  mainActionButton: string;
};

const getSegmentLimitsModalConfig = (
  translations: SegmentLimitsTranslations,
  details: NonNullable<
    CreateTradeOfferMutation$data['createTradeOffer']
  >['details'],
  t: TFunction,
  locale: string,
): Record<CreateTradeOfferErrorCode, ModalParams> => {
  const { currency } = getAppConfig();

  return {
    INVESTMENT_NORMAL_LIMIT_REACHED: {
      title: translations.modalTitle,
      limitAvailable: t(translations.normalLimitAvailable, {
        remainingBalance: intlFiatValueFormatting(
          details?.remainingBalanceAmount?.value || 0,
          {
            currency,
            locale,
          },
        ),
      }),
      limitReached: t(translations.limitReached, {
        currentInvested: intlFiatValueFormatting(
          details?.currentInvestedAmount?.value || 0,
          {
            currency,
            locale,
          },
        ),
        maxLimit: intlFiatValueFormatting(details?.maxLimitAmount?.value || 0, {
          currency,
          locale,
        }),
      }),

      limitContinuation: translations.limitContinuation,
      mainActionButton: translations.tryAgain,
    },
    INVESTMENT_ZERO_LIMIT_REACHED: {
      title: translations.modalTitle,
      limitAvailable: t(translations.zeroLimitAvailable, {
        remainingBalance: intlFiatValueFormatting('0', {
          currency,
          locale,
        }),
      }),
      limitReached: t(translations.limitReached, {
        currentInvested: intlFiatValueFormatting(
          details?.currentInvestedAmount?.value || 0,
          {
            currency,
            locale,
          },
        ),
        maxLimit: intlFiatValueFormatting(details?.maxLimitAmount?.value || 0, {
          currency,
          locale,
        }),
      }),

      limitContinuation: translations.limitContinuation,
      mainActionButton: translations.sell,
    },
  };
};

export const SegmentLimitsModal: SegmentLimitsModalProps = ({
  type,
  details,
  assetId,
  onClose,
}) => {
  const { t, locale } = useClientTranslation();

  const translations = getSegmentLimitTranslations(t);

  const config = getSegmentLimitsModalConfig(translations, details, t, locale)[
    type
  ];

  const handleClose = () => {
    removeInputValueFromLocalStorage();

    onClose();
  };

  return (
    <DialogBase
      id="base-modal"
      size="small"
      onMobileFullScreen
      visual="secondary"
      title={config.title}
      showTitleBelowHeader
      disableCloseButton={true}
      topIcon={
        <div
          className={css({
            height: 80,
            width: 80,
            borderRadius: '50%',
            backgroundColor: 'negative.fill_focused',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: 'medium',
          })}
        >
          <RegularDismissCircle
            size={48}
            data-element="icon-visual"
            data-testid="icon-error"
            color="negative.text_primary"
          />
        </div>
      }
    >
      <div className={containerStyles}>
        <div
          className={css({
            display: 'flex',
            flexDirection: 'column',
            gap: 'medium',
            alignItems: 'center',
            py: 'medium',
            px: 'extra_small_2',
            color: 'neutrals.text_secondary',
            fontStyle: 'body.medium',
            fontWeight: 'body.medium',
            lineHeight: 'body.medium',
          })}
        >
          <p>{config.limitReached}</p>
          <p>{config.limitAvailable}</p>
          <p>{config.limitContinuation}</p>
        </div>
      </div>

      <div
        className={stack({
          marginInlineStart: 'auto',
          marginInlineEnd: 'auto',
          maxWidth: '390px',
          width: '100%',
          gap: 'small',
          alignItems: 'center',
        })}
      >
        <HyperLinkButtonBase
          prefetch
          visual="accent"
          data-testid="main-action-link"
          to={
            type === 'INVESTMENT_ZERO_LIMIT_REACHED'
              ? getAssetTradeUrl(assetId, { type: 'sell' })
              : ''
          }
          onClick={handleClose}
        >
          {config.mainActionButton}
        </HyperLinkButtonBase>

        <HyperLinkButtonBase
          prefetch
          visual="ghost"
          data-testid="portfolio-link"
          to={NEXT_ROUTES.PORTFOLIO}
          onClick={handleClose}
        >
          {translations.goToPortfolio}
        </HyperLinkButtonBase>
      </div>
    </DialogBase>
  );
};
